import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormProvider } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import { FormSection, MaskedInput, FormRow as Row, Select, Toggle } from '~/components';
import { ButtonFooter } from '~/components/buttons/ButtonFooter';
import { LIMITED_PHONE_COUNTRY_CODE } from '~/constants';
import { useCustomForm } from '~/hooks/useCustomForm';
import { PHONE_TYPE_OPTIONS } from '../constants';
import type { profileFormMachine } from '../profileForm.machine';
import { PhoneFormValues, phoneNumSchema } from '../types';

const TAGS = CT.profile.contactInfo.phone.form;

interface FormProps {
  actor: ActorRefFrom<typeof profileFormMachine>;
  addNumber: (number: PhoneFormValues) => void;
  editNumber: (number: PhoneFormValues) => void;
}

export const PhoneForm = (props: FormProps) => {
  const { addNumber, editNumber, actor } = props;
  const phoneNumberForm = useSelector(actor, (state) => state.context.phoneNumberForm);
  const create = useSelector(actor, (state) => state.context.modal.create);

  const methods = useCustomForm<PhoneFormValues>({
    resolver: zodResolver(phoneNumSchema),
    defaultValues: phoneNumberForm,
  });
  const { handleSubmit } = methods;

  const onSubmitError = (err: unknown) => console.debug('Phone form validation error:', err);
  const onSubmitSuccess = (values: PhoneFormValues) => {
    if (create) {
      addNumber(values);
    } else {
      editNumber(values);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-5"
        data-cy={TAGS.form}
        onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
        id={TAGS.form}
      >
        <FormSection componentsPerLine={2}>
          <Row>
            <Select
              name={'phoneType'}
              label={'Phone Type'}
              cyTag={TAGS.typeSelect}
              options={PHONE_TYPE_OPTIONS}
              required
            />
          </Row>
          <Row>
            <Toggle
              cyTag={TAGS.primaryCheck}
              name={'primaryFlag'}
              label={'Primary phone'}
              description={'Make this the primary phone on the account'}
            />
          </Row>
          <Row>
            <Select
              name={'countryCode'}
              label={'Country code'}
              cyTag={TAGS.countryCodeSelect}
              options={LIMITED_PHONE_COUNTRY_CODE}
            />
          </Row>
          <Row>
            {/* [MCV-862] Mark required so the `*` indicator is visible.
                Schema already rejects empty (min 12 + regex), but without
                a visual required marker users were missing the validation
                message and thinking empty submissions were allowed. */}
            <MaskedInput
              name={'phoneNum'}
              label={'Phone Number'}
              cyTag={TAGS.NumInput}
              mask={'###-###-####'}
              replacement={{ '#': /\d/ }}
              required
            />
          </Row>
        </FormSection>
        <ButtonFooter primaryButton={{ label: 'Save', cyTag: TAGS.submitBtn, isSubmit: true }} />
      </form>
    </FormProvider>
  );
};
